<template>
  <div v-show="isCustomFieldValid">
    <label
      v-if="!props.flat && !props.hideLabel"
      class="input-label tw-flex tw-items-center"
      :for="`${customField?.name}`"
    >
      {{ customField?.name }}
      <span
        v-if="props.customField.required"
        class="tw-ml-0.5 tw-text-sm tw-text-red"
      >
        *
      </span>
      <help-text v-if="!!customField.description" class="tw-mb-0.5 tw-ml-1">
        {{ customField.description }}
      </help-text>
    </label>
    <p-date-input
      :model-value="localVal"
      :class="{
        'tw-h-full': props.flat,
      }"
      trigger-container-class="tw-h-full"
      placeholder=""
      :title="`${customField?.name}`"
      dense
      is-dialog
      :readonly="props.readonly"
      :tabindex="props.readonly ? -1 : 0"
      :clearable="!props.customField.required"
      :allow-time="allowTime"
      :popup-props="{
        offset: [-10, 0],
        anchor: 'bottom left',
      }"
      @change="onValueChange"
      @clear="onClear"
      @keyup.t="setStartDateToToday"
    >
      <template #trigger="{ label }">
        <slot name="trigger" :value="localVal" :label="label"></slot>
      </template>
    </p-date-input>

    <span
      v-if="!!errorMessage && showErrorMessage && !props.flat"
      class="form-input__error-message"
    >
      {{ errorMessage }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { DateTimeRange } from '@/models/common.models';
import { ref, computed, watch } from 'vue';
import HelpText from '../design-system/misc/HelpText.vue';
import { toServerDate } from '@/utils/date';
import dayjs from '@/utils/dayjs';
import { CustomField, CustomFieldType, CustomFieldDateSubType } from '@/api';

interface Props {
  customField: CustomField & { required?: boolean };
  value?: string;
  flat?: boolean;
  hideLabel?: boolean;
  readonly?: boolean;
}
interface Emits {
  (e: 'value-updated', value: string | null): void;
}

const props = withDefaults(defineProps<Props>(), {});
const emit = defineEmits<Emits>();

const isCustomFieldValid = computed(() => {
  return props.customField.type === CustomFieldType.DATE;
});

const allowTime = computed(() => {
  return props.customField.subType === CustomFieldDateSubType.DATETIME;
});

const localVal = ref<string>(props.value || '');
watch(
  () => props.value,
  () => {
    localVal.value = props.value || '';
  }
);

const showErrorMessage = ref(false);

const errorMessage = computed(() => {
  if (!localVal.value && props.customField.required) {
    return 'This field is required';
  }
  return '';
});

watch(localVal, () => {
  showErrorMessage.value = false;
});

function onClear() {
  if (props.customField.required) return;
  localVal.value = '';
  onValueChange('');
}

function onValueChange(value: string | DateTimeRange) {
  if (!value && props.customField.required) return;
  if (typeof value === 'object') return;
  localVal.value = value;
  if (errorMessage.value) {
    showErrorMessage.value = true;
    return;
  }
  if (props.value === localVal.value || (!props.value && !localVal.value))
    return;
  emit('value-updated', localVal.value || null);
}

const isValid = computed(() => {
  return !errorMessage.value;
});

function validate() {
  showErrorMessage.value = !isValid.value;
}

function removeError() {
  showErrorMessage.value = false;
}

const setStartDateToToday = () => {
  localVal.value = toServerDate(dayjs().startOf('day').toDate()) as string;
  onValueChange(localVal.value);
};

defineExpose({
  validate,
  removeError,
  isValid,
});
</script>
